import * as React from 'react';
import classnames from 'classnames';

import { withModal } from 'components/modal/ModalHOC';
import HelpNote from 'components/help-note/HelpNote';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import Tooltip from 'components/Tooltip';

import FormFieldModal from '../form-field-modal/FormFieldModal';
import FormRequiredMark from '../form-required-mark/FormRequiredMark';
import Validatable from '../Validatable';

import ValidatorContext from '../../../context/ValidatorContext';

import { __t, applyTranslations } from 'utils/language';
import { expandDataProps } from 'utils/helper/common';

import type { Data, ElementDirection, FormValidator, TooltipTextAlign, TooltipWidth } from 'types';

import './form-field.scss';

applyTranslations(require('./form-field.i18n.json'));

const TooltipWithModal = withModal(Tooltip, {
  modalContent: FormFieldModal
});

type Props = {
  showRequiredMark?: boolean,
  showOptionalMark?: boolean,
  showValidationIcon?: boolean,
  direction?: 'row' | 'column',
  labelType?: 'highlighted' | 'normal',
  labelSize?: 'fixed' | 'collapsed' | 'custom', // this is used to control spacing between label and input for row direction,
  fontSize?: 'normal' | 'small',
  label?: string,
  labelPopoverComponent?: React.ReactNode,
  sublabel?: string | React.ReactNode,
  inline?: boolean,
  validationErrorOverflow?: boolean,
  children: React.ReactNode,
  validators?: Array<FormValidator>,
  validationAlign?: 'center' | 'top',
  helpNote?: string,
  isDisabled?: boolean,
  tooltipMessage?: string,
  tooltipWidth?: TooltipWidth,
  tooltipTextAlign?: TooltipTextAlign,
  tooltipDirection?: ElementDirection,
  comment?: React.ReactNode,
  formFieldModalKey?: string,
  immediateValidationError?: string,
  className?: string,
  columnClassName?: string,
  labelClassName?: string,
  errorClassName?: string,
  data?: Data,
  error?: string | null,
  formFieldModal?: any,
  onClick?: (a: React.SyntheticEvent<HTMLElement>) => void
};

type State = {
  valid: boolean | undefined | null,
  messages: Array<string>
};

class FormField extends React.Component<Props, State> {
  static defaultProps = {
    showRequiredMark: false,
    showValidationIcon: true,
    showOptionalMark: false,
    direction: 'row',
    labelType: 'normal',
    labelSize: 'fixed',
    validationAlign: 'center',
    tooltipWidth: 'normal',
    tooltipTextAlign: 'center',
    fontSize: 'normal',
    label: ''
  };

  state = {
    valid: null,
    messages: []
  };

  resetValidationState = (): void => {
    this.setState({
      valid: null,
      messages: []
    });
  };

  updateValidationState = (state: State): void => {
    const { valid, messages } = state;
    this.setState({ valid, messages });
  };

  renderLabel(labelClasses: string): React.ReactNode {
    const { label, showRequiredMark, showOptionalMark } = this.props;

    return (
      <span className={labelClasses}>
        {label}
        {
          showOptionalMark &&
            <span className="form-field__optional">{`(${__t('optional_field')})`}</span>
        }
        {
          showRequiredMark &&
            <FormRequiredMark />
        }
      </span>
    );
  }

  render(): React.ReactNode {
    const { valid, messages } = this.state;
    const {
      label,
      labelType,
      labelSize,
      labelPopoverComponent,
      sublabel,
      children,
      fontSize,
      validators,
      direction,
      validationAlign,
      showValidationIcon,
      isDisabled,
      inline,
      helpNote,
      tooltipMessage,
      tooltipWidth,
      tooltipTextAlign,
      tooltipDirection,
      comment,
      formFieldModalKey,
      immediateValidationError,
      className,
      columnClassName,
      labelClassName,
      errorClassName,
      data,
      error,
      validationErrorOverflow,
      formFieldModal,
      onClick
    } = this.props;

    const firstValidationMessage = messages.length ? messages[0] : '';

    const fieldClasses = classnames(className, {
      'form-field': true,
      'form-field--inline': inline,
      [`form-field--direction-${direction}`]: direction,
      [`form-field--font-size-${fontSize}`]: fontSize
    });

    const columnClasses = classnames('form-field__column', columnClassName, {
      [`form-field__column--direction-${direction}`]: direction,
      [`form-field__column--size-${labelSize}`]: labelSize
    });

    const contentClasses = classnames(columnClasses, {
      'form-field__column--content': true,
      'form-field__column--with-validation': showValidationIcon,
      [`form-field__column--validation-${validationAlign}`]: validationAlign,
      'form-field__column--validation-visible': valid != null
    });

    const labelClasses = classnames('form-field__label', labelClassName, {
      'form-field__label--is-disabled': isDisabled,
      [`form-field__label--${labelType}`]: labelType
    });

    const sublabelClasses = classnames('form-field__sublabel', {
      [`form-field__sublabel--font-size-${fontSize}`]: fontSize
    });

    const valueClasses = classnames({
      'form-field__value': true,
      'form-field__value--invalid': valid === false || immediateValidationError
    });

    const validationErrorClasses = classnames('form-field__error', errorClassName, {
      'form-field__error--overflow': validationErrorOverflow
    });

    const HelpModal = formFieldModalKey ? TooltipWithModal : formFieldModal;

    return (
      <div
        className={fieldClasses}
        {...expandDataProps(data)}
        onClick={onClick}
      >
        <div className="form-field__columns">
          {
            label &&
              <div className={`${columnClasses} form-field__column--label`}>
                {this.renderLabel(labelClasses)}
                {
                  tooltipMessage &&
                    <span className="form-field__tooltip-mark">
                      <Tooltip
                        message={tooltipMessage}
                        width={tooltipWidth}
                        textAlign={tooltipTextAlign}
                        showQuestionMark
                        tooltipDirection={tooltipDirection}
                      />
                    </span>
                }
                {labelPopoverComponent}
                {
                  HelpModal && (
                    <div className="form-field__global-modal-trigger">
                      <HelpModal
                        message={__t('form_field.please_click')}
                        width={tooltipWidth}
                        promptdisabled
                        modalContentProps={{ formFieldModalKey }}
                        showQuestionMark
                      />
                    </div>
                  )
                }
                {comment || null}
              </div>
          }
          {
            sublabel && (
              <div className={`${columnClasses} form-field__column--label`}>
                <div className={sublabelClasses}>
                  {sublabel}
                </div>
              </div>
            )
          }
          <ValidatorContext.Consumer>
            {({ registerValidators, unregisterValidators }) => (
              <Validatable
                validators={validators}
                onValidate={this.updateValidationState}
                onResetValidation={this.resetValidationState}
                registerValidators={registerValidators}
                unregisterValidators={unregisterValidators}
              >
                <div className={contentClasses}>
                  <div className={valueClasses}>
                    {children}
                  </div>
                  {
                    showValidationIcon &&
                      <div
                        className="form-field__validation"
                      >
                        {
                          valid != null &&
                            <SVGDynamicIcon
                              name={valid === true ? 'checkmark-custom-fg' : 'cross-custom-fg'}
                              width={valid === true ? 16 : 13}
                              height={valid === true ? 14 : 13}
                              customColor={valid === true ? 'primary' : 'red'}
                            />
                        }
                      </div>
                  }
                </div>
              </Validatable>
            )}
          </ValidatorContext.Consumer>
        </div>
        {
          (immediateValidationError || valid === false || error) &&
            <div className={validationErrorClasses}>
              {immediateValidationError || firstValidationMessage || error}
            </div>
        }
        {
          helpNote &&
            <HelpNote
              message={helpNote}
            />
        }
      </div>
    );
  }
}

export default FormField;
